<template>
  <div class="ui-input">
    <input
      v-if="formFieldName"
      type="hidden"
      :name="formFieldName"
      :value="valueAsJson"
    />

    <div
      v-if="valueArray.length"
      class="uk-alert-warning"
      style="font-size: 14px; color: #000;"
      uk-alert
    >
      <b>Files uploaded:</b>
      <ul style="margin-top: 0; margin-bottom: 0;">
        <li v-for="(val, idx) in valueArray" :key="idx">
          {{ val }}
          <button
            type="button"
            class="uk-button uk-button-link uk-button-small"
            aria-label="Remove"
            @click="removeFile(val)"
          >
            <span uk-icon="trash"></span>
          </button>
        </li>
      </ul>
    </div>

    <uppy-dashboard
      :uppy="uppy"
      :props="dashboardProps"
    />
  </div>
</template>

<script>
import { Dashboard } from '@uppy/vue'
import Uppy from '@uppy/core'
import AwsS3 from '@uppy/aws-s3'

export default {
  name: 'UiFormUppy',

  components: {
    UppyDashboard: Dashboard,
  },

  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },

  inject: {
    ngForm: {
      default: '',
    },
  },

  props: {
    modelValue: {
      type: Array,
      default() {
        return []
      },
    },
    paramsUrl: {
      type: String,
      required: true,
    },
    formFieldName: {
      type: String,
    },
    minNumFiles: {
      type: Number,
      default: 0,
    },
    maxNumFiles: {
      type: Number,
      default: 1,
    },
    allowedTypes: {
      type: Array,
    },
    autoUpload: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      uppy: null,
    }
  },

  computed: {
    multipleFilesAllowed() {
      return this.maxNumFiles === -1 || this.maxNumFiles > 1
    },

    valueArray() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue
      }
      if (this.modelValue) {
        return [this.modelValue]
      }
      return []
    },

    valueAsJson() {
      return JSON.stringify(this.valueArray)
    },

    shouldShowUploader() {
      return this.valueArray.length < this.maxNumFiles
    },

    dashboardProps() {
      const browseText = this.multipleFilesAllowed
        ? 'Drop files here, paste, or %{browse}'
        : 'Drop file here, paste, or %{browse}'

      return {
        theme: 'light',
        doneButtonHandler: null,
        showRemoveButtonAfterComplete: true,
        proudlyDisplayPoweredByUppy: false,
        locale: {
          strings: {
            dropPasteFiles: browseText,
          },
        },
      }
    },
  },

  methods: {
    removeFile(val) {
      let values = this.valueArray.slice()
      const idx = values.indexOf(val)
      if (idx !== -1) {
        values.splice(idx, 1)
        this.modelValue = values
        this.$emit('update:modelValue', values)
      }
    },
  },

  beforeDestroy() {
    this.uppy.close()
  },

  created() {
    const uppy = new Uppy({
      debug: true,
      autoProceed: this.autoUpload,
      allowMultipleUploadBatches: this.multipleFilesAllowed,
      restrictions: {
        // bytes = {X}MB * 1E+6;  1E+6 === 1,000,000
        maxFileSize: 50 * 1000000,
        maxNumberOfFiles: this.maxNumFiles === -1 ? null : this.maxNumFiles,
        minNumberOfFiles: this.minNumFiles,
        allowedFileTypes: this.allowedTypes,
      },
    }).use(AwsS3, {
      getUploadParameters: (file) => {
        return fetch(this.paramsUrl, {
          method: 'post',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
          }),
        })
          .then(response => response.json())
          .then((data) => {
            if ('error' in data) {
              alert(data.error)
              return false
            }
            return {
              method: data.method,
              url: data.url,
              fields: data.fields,
            }
          })
      },
    })

    uppy.on('upload-success', (file) => {
      let values = this.valueArray.slice()
      if (!this.multipleFilesAllowed) {
        values = [file.meta.filename]
      } else {
        values.push(file.meta.filename)
      }
      this.$emit('update:modelValue', values)
    })

    this.uppy = uppy
  },
}
</script>

<style src="@uppy/core/dist/style.css"></style>
<style src="@uppy/dashboard/dist/style.css"></style>
